import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Image as ImageIcon,
  Plus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  LayoutTemplate,
  X,
} from 'lucide-react';
import { useState } from 'react';

import type { BannerPlacement, CmsBanner } from '@terra/api-client';
import { Badge, EmptyState, Input, Select, Spinner } from '@terra/ui';

import { api } from '../lib/api';

const PLACEMENTS: { value: BannerPlacement; label: string }[] = [
  { value: 'HOME_HERO', label: 'Home — Hero' },
  { value: 'HOME_STRIP', label: 'Home — Promo Strip' },
  { value: 'CATEGORY_TOP', label: 'Category — Top' },
  { value: 'SPONSORED_SHOWCASE', label: 'Home — Sponsored Showcase' },
];

const emptyForm = {
  placement: 'HOME_HERO' as BannerPlacement,
  imageUrl: '',
  linkUrl: '',
  alt: '',
  position: '0',
  file: null as File | null,
  previewUrl: null as string | null,
};

export function BannersPage() {
  const qc = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [showAdd, setShowAdd] = useState(false);

  const { data: banners, isLoading } = useQuery({
    queryKey: ['cms-banners-admin'],
    queryFn: () => api.cms.listBanners(),
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['cms-banners-admin'] });

  const create = useMutation({
    mutationFn: async () => {
      let finalImageUrl = form.imageUrl.trim();

      if (form.file) {
        const sig = await api.files.getUploadSignature({ folder: 'banners' });
        const fd = new FormData();
        fd.append('file', form.file);
        fd.append('api_key', sig.apiKey);
        fd.append('timestamp', String(sig.timestamp));
        fd.append('signature', sig.signature);
        fd.append('folder', sig.folder);

        const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloudName}/image/upload`, {
          method: 'POST',
          body: fd,
        });

        const out = (await res.json()) as { secure_url?: string };
        if (out.secure_url) {
          finalImageUrl = out.secure_url;
        } else {
          throw new Error('Upload failed');
        }
      }

      return api.cms.createBanner({
        placement: form.placement,
        imageUrl: finalImageUrl,
        linkUrl: form.linkUrl.trim() || null,
        alt: form.alt.trim() || null,
        position: Number(form.position) || 0,
      });
    },
    onSuccess: () => {
      setForm(emptyForm);
      setShowAdd(false);
      invalidate();
    },
  });

  const toggleActive = useMutation({
    mutationFn: (b: CmsBanner) => api.cms.updateBanner(b.id, { active: !b.active }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.cms.deleteBanner(id),
    onSuccess: invalidate,
  });

  const valid = form.file != null || /^https?:\/\//.test(form.imageUrl.trim());
  const list = banners ?? [];

  return (
    <div className="space-y-6 pb-8">
      {/* Add form */}
      <AnimatePresence>
        {showAdd && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm"
          >
            <div className="border-border/40 mb-5 flex items-center gap-2 border-b pb-4">
              <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
                <LayoutTemplate className="h-4 w-4" />
              </div>
              <h3 className="text-ink text-[15px] font-extrabold">New Banner</h3>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <Select
                  label="Placement"
                  value={form.placement}
                  onValueChange={(v) => setForm((f) => ({ ...f, placement: v as BannerPlacement }))}
                  options={PLACEMENTS}
                />
              </div>
              <div>
                <label className="text-ink mb-1 block text-[13px] font-bold">Image</label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = (e: any) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setForm((f) => ({
                            ...f,
                            file,
                            previewUrl: URL.createObjectURL(file),
                            imageUrl: '',
                          }));
                        }
                      };
                      input.click();
                    }}
                    className="bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/20 flex h-10 items-center gap-2 rounded-xl px-4 text-[13px] font-bold transition-all"
                  >
                    Browse
                  </button>
                  <span className="text-ink-muted text-[12px] font-medium">
                    {form.file ? '1 File selected' : 'No file chosen'}
                  </span>
                </div>
                {form.previewUrl && (
                  <div className="relative mt-2 inline-block">
                    <img
                      src={form.previewUrl}
                      alt=""
                      className="h-16 w-auto rounded-lg border object-cover shadow-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, file: null, previewUrl: null }))}
                      className="absolute -right-2 -top-2 grid h-5 w-5 place-items-center rounded-full bg-red-500 text-white shadow-sm"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
                {!valid && !form.file && form.imageUrl.length > 0 && (
                  <p className="mt-1 text-[11px] font-medium text-red-500">
                    Upload an image or enter a valid URL.
                  </p>
                )}
              </div>
              <div>
                <Input
                  label="Link URL (optional)"
                  placeholder="/search or /category/..."
                  value={form.linkUrl}
                  onChange={(e) => setForm((f) => ({ ...f, linkUrl: e.target.value }))}
                />
              </div>
              <div>
                <Input
                  label="Alt text (optional)"
                  value={form.alt}
                  onChange={(e) => setForm((f) => ({ ...f, alt: e.target.value }))}
                />
              </div>
              <div>
                <Input
                  label="Position (order)"
                  type="number"
                  value={form.position}
                  onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))}
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => create.mutate()}
                  disabled={!valid || create.isPending}
                  className="bg-ink hover:bg-ink/90 flex h-10 w-full items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {create.isPending ? 'Creating...' : 'Create Banner'}
                </button>
              </div>
            </div>
            {create.isError && (
              <p className="mt-3 text-[12px] font-medium text-red-500">
                Failed to create banner. Try again.
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Banner List */}
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        <div className="border-border/60 bg-surface-subtle/30 flex items-center justify-between border-b px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
              <ImageIcon className="h-4 w-4" strokeWidth={2} />
            </div>
            <span className="text-ink text-[14px] font-bold">All Banners</span>
            <span className="text-ink-muted ml-auto text-[12px] font-bold">
              {list.length} total
            </span>
          </div>
          <button
            onClick={() => setShowAdd((v) => !v)}
            className="bg-ink hover:bg-ink/90 flex h-10 items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            {showAdd ? 'Close Form' : '+ Add Banner'}
          </button>
        </div>

        <div className="p-5">
          {isLoading ? (
            <div className="grid min-h-[200px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : list.length === 0 ? (
            <div className="border-border/60 bg-surface-subtle/30 rounded-2xl border border-dashed py-12">
              <EmptyState
                icon={<ImageIcon className="text-ink-muted h-10 w-10" />}
                title="No banners yet"
                description="Create your first banner to control the storefront homepage."
              />
            </div>
          ) : (
            <div className="space-y-3">
              {list.map((b) => (
                <div
                  key={b.id}
                  className="border-border/60 bg-surface hover:border-ink/60 hover:bg-surface-subtle/20 flex items-center gap-4 rounded-xl border p-3 transition-colors"
                >
                  <div className="border-border/40 bg-surface-subtle h-14 w-24 shrink-0 overflow-hidden rounded-lg border">
                    <img
                      src={b.imageUrl}
                      alt={b.alt ?? ''}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <Badge tone="neutral">{b.placement.replace(/_/g, ' ')}</Badge>
                      <Badge tone={b.active ? 'success' : 'neutral'} dot>
                        {b.active ? 'Active' : 'Inactive'}
                      </Badge>
                      <span className="text-ink-muted text-[11px] font-bold">pos {b.position}</span>
                    </div>
                    <div className="text-ink-muted truncate text-[12px] font-medium">
                      {b.linkUrl ?? '— no link —'}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      onClick={() => toggleActive.mutate(b)}
                      className={`flex h-8 items-center gap-1.5 rounded-lg border px-3 text-[12px] font-bold transition-colors ${
                        b.active
                          ? 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100'
                          : 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'
                      }`}
                    >
                      {b.active ? (
                        <ToggleRight className="h-3.5 w-3.5" />
                      ) : (
                        <ToggleLeft className="h-3.5 w-3.5" />
                      )}
                      {b.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm('Delete this banner?')) remove.mutate(b.id);
                      }}
                      className="text-ink-muted flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={2} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
